import { type CostCoverage, formatMoney, type LookupEntry } from '@k-management/shared';

/** Where a voice of Tipologie di riga sits: sub-type, type with sub-types or type alone. */
export type TypeLevel = 'child' | 'parent' | 'single';

/** Text and colour of the column «Costi» (decision of 29/09/2026). */
export function coverageLabel(
  coverage: CostCoverage,
  parentName: string | null,
): { text: string; className: string } {
  const pending = coverage.pending > 0 ? ` · ${coverage.pending} da completare` : '';
  const children = coverage.children;
  if (coverage.status === 'licence') return { text: 'listino', className: 'pill muted' };
  if (children) {
    if (children.none > 0) {
      return {
        text: `${children.none} ${children.none === 1 ? 'sottotipologia' : 'sottotipologie'} su ${children.total} senza costi${pending}`,
        className: 'pill bad',
      };
    }
    if (children.commonOnly > 0) {
      return {
        text: `${children.commonOnly} su ${children.total} solo con i costi comuni${pending}`,
        className: 'pill warn',
      };
    }
    return { text: `tutte con costi${pending}`, className: 'pill ok' };
  }
  switch (coverage.status) {
    case 'own':
      return {
        text: `${coverage.ready} ${coverage.ready === 1 ? 'costo' : 'costi'}${pending}`,
        className: 'pill ok',
      };
    case 'common':
      return {
        text: `solo costi di ${parentName ?? 'tipologia'}${pending}`,
        className: 'pill warn',
      };
    default:
      return { text: `nessun costo${pending}`, className: 'pill bad' };
  }
}

/** The costs of a voice, in its form: what a quote line of this type would offer. */
export function LinkedCosts({
  coverage,
  level,
  parentName,
  units,
  onAddCost,
}: {
  coverage: CostCoverage;
  level: TypeLevel;
  parentName: string | null;
  units: LookupEntry[];
  onAddCost?: () => void;
}) {
  if (coverage.status === 'licence') {
    return (
      <p className="muted small">
        Le licenze non hanno costi previsti: il prezzo viene dal listino.
      </p>
    );
  }
  const label = coverageLabel(coverage, parentName);
  const scope = (own: boolean) =>
    !own
      ? `tutta la tipologia ${parentName ?? ''}`.trim()
      : level === 'child'
        ? 'questa sottotipologia'
        : level === 'parent'
          ? 'tutte le sottotipologie'
          : 'questa tipologia';
  return (
    <section className="stack linked-costs" aria-label="Costi collegati">
      <div className="row">
        <h3>Costi collegati</h3>
        <span className={label.className}>{label.text}</span>
      </div>
      {coverage.costs.length === 0 ? (
        <p className="muted small">
          Nessuna voce del Listino Passività (Base) vale per questa voce
          {level === 'parent' ? ' nel suo insieme' : ''}: una riga di preventivo di questo tipo non
          avrebbe costi da proporre.
        </p>
      ) : (
        <div className="table-wrap card flush">
          <table className="data-table">
            <thead>
              <tr>
                <th>Profilo</th>
                <th>Unità</th>
                <th className="numeric">Costo unitario</th>
                <th>Vale per</th>
              </tr>
            </thead>
            <tbody>
              {coverage.costs.map((cost) => (
                <tr key={cost.code} className={cost.ready ? undefined : 'inactive'}>
                  <td>{cost.description}</td>
                  <td>
                    {cost.unitCode
                      ? (units.find((unit) => unit.code === cost.unitCode)?.description ??
                        cost.unitCode)
                      : '—'}
                  </td>
                  <td className="numeric">
                    {cost.ready && cost.unitCost !== null ? (
                      formatMoney(cost.unitCost)
                    ) : (
                      <span className="pill warn">da completare</span>
                    )}
                  </td>
                  <td>{scope(cost.scope === 'own')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {onAddCost && (
        <div>
          <button type="button" className="small" onClick={onAddCost}>
            + Aggiungi costo
          </button>
        </div>
      )}
    </section>
  );
}
